import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import axios from 'axios'
import Snackbar from '@mui/material/Snackbar'
import Alert, { type AlertColor } from '@mui/material/Alert'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import CloseIcon from '@mui/icons-material/Close'
import { api } from '../services/API'
import { applyFieldErrorsToActiveForm, formatFieldErrors, getBackendError } from '../utils/formErrors'

interface NotificationContextValue {
    /** `action`, quando indicado, fica visível junto à mensagem (sem auto-hide,
     *  já que normalmente exige uma decisão do utilizador) — ex: um botão de
     *  navegação para resolver o problema reportado. O botão de fechar mantém-se
     *  sempre disponível ao lado da acção. */
    notify: (message: string, severity?: AlertColor, action?: ReactNode) => void
}

interface QueuedNotification {
    key: number
    message: string
    severity: AlertColor
    action?: ReactNode
}

const NotificationContext = createContext<NotificationContextValue | undefined>(undefined)

const FORBIDDEN_FALLBACK = 'Não tem permissão para executar esta acção.'

export function NotificationProvider({ children }: { children: ReactNode }) {
    // Fila de notificações — mostradas uma de cada vez, por ordem de chegada.
    // Sem fila, duas chamadas a notify() em sucessão rápida (ex: o intercetor
    // de 403 abaixo e o catch local do próprio pedido) fariam a segunda
    // substituir a primeira antes de ser lida.
    const [queue, setQueue] = useState<QueuedNotification[]>([])
    const [current, setCurrent] = useState<QueuedNotification | undefined>(undefined)
    const [open, setOpen] = useState(false)
    const nextKey = useRef(0)
    // Texto+severidade da notificação visível/agendada mais recente — permite
    // ignorar duplicados exactos chegados em sucessão rápida (ex: o intercetor
    // de 403 abaixo e o catch local do mesmo pedido tentam mostrar a mesma
    // mensagem). Limpa quando essa notificação termina (handleExited), para
    // que a mesma mensagem possa voltar a aparecer numa acção futura.
    const lastMessageRef = useRef<{ message: string; severity: AlertColor } | null>(null)
    // Quando um 422 de validação já foi mostrado inline nos campos do formulário
    // activo, o `catch` local do ecrã ainda vai tentar mostrar o seu toast
    // genérico ("não foi possível gravar") logo a seguir — este marcador
    // suprime esse próximo toast de erro, para não duplicar a informação.
    const suppressNextErrorUntilRef = useRef(0)

    const notify = useCallback((message: string, severity: AlertColor = 'success', action?: ReactNode) => {
        if (severity === 'error' && Date.now() < suppressNextErrorUntilRef.current) {
            suppressNextErrorUntilRef.current = 0
            return
        }
        if (lastMessageRef.current?.message === message && lastMessageRef.current?.severity === severity) {
            return
        }
        lastMessageRef.current = { message, severity }
        nextKey.current += 1
        setQueue((prev) => [...prev, { key: nextKey.current, message, severity, action }])
    }, [])

    useEffect(() => {
        if (queue.length > 0 && !current) {
            setCurrent(queue[0])
            setQueue((prev) => prev.slice(1))
            setOpen(true)
        } else if (queue.length > 0 && current && open) {
            // Já há uma notificação visível — fecha-a; a próxima entra quando
            // a transição de saída terminar (handleExited).
            setOpen(false)
        }
    }, [queue, current, open])

    const handleClose = (_event?: unknown, reason?: string) => {
        if (reason === 'clickaway') return
        setOpen(false)
    }

    const handleExited = () => {
        setCurrent(undefined)
        lastMessageRef.current = null
    }

    // Intercetor global: qualquer pedido que o backend recuse por falta de
    // permissão (403) mostra sempre a mensagem explicativa devolvida pela API
    // — funciona mesmo nos ecrãs cujo catch local ainda não trata o erro
    // devidamente, sem precisar de tocar em cada um deles.
    useEffect(() => {
        const interceptorId = api.interceptors.response.use(
            (response) => response,
            (error) => {
                if (axios.isAxiosError(error)) {
                    const status = error.response?.status
                    if (status === 403) {
                        // Módulo fora do plano da subscrição (MODULO_NAO_DISPONIVEL):
                        // o bloqueio já se reflecte na navegação (a Sidebar esconde o
                        // módulo) e no próprio ecrã do módulo — um toast a cada pedido
                        // de fundo que lhe toque (ex.: o painel a carregar dados de
                        // estacionamento) é só ruído. Os restantes 403 (falta de
                        // permissão) continuam a avisar.
                        const moduloBloqueado = error.response?.data?.code === 'MODULO_NAO_DISPONIVEL'
                        // Pedidos de melhor-esforço podem optar por não mostrar o toast
                        // (o seu catch local já trata a ausência de permissão).
                        if (!moduloBloqueado && !error.config?.suppressForbiddenToast) {
                            notify(error.response?.data?.message || FORBIDDEN_FALLBACK, 'error')
                        }
                    } else if (status === 422 || status === 400) {
                        // Erros de validação campo-a-campo (@Valid no backend):
                        // aplica-os por baixo de cada campo do formulário activo;
                        // se não houver formulário a recebê-los, mostra-os num
                        // toast. Em qualquer dos casos, suprime o toast genérico
                        // ("não foi possível gravar") que o catch local do ecrã
                        // ainda vai tentar mostrar a seguir.
                        const fields = getBackendError(error)?.fields
                        if (fields && Object.keys(fields).length > 0) {
                            if (!applyFieldErrorsToActiveForm(fields)) {
                                notify(formatFieldErrors(fields), 'error')
                            }
                            suppressNextErrorUntilRef.current = Date.now() + 300
                        }
                    }
                }
                return Promise.reject(error)
            }
        )
        return () => api.interceptors.response.eject(interceptorId)
    }, [notify])

    const value = useMemo(() => ({ notify }), [notify])

    return (
        <NotificationContext.Provider value={value}>
            {children}
            <Snackbar
                key={current?.key}
                open={open}
                autoHideDuration={current?.action ? null : 4000}
                onClose={handleClose}
                slotProps={{ transition: { onExited: handleExited } }}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    severity={current?.severity ?? 'success'}
                    variant="filled"
                    sx={{ width: '100%' }}
                    action={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {current?.action}
                            <IconButton size="small" color="inherit" onClick={() => handleClose()} aria-label="Fechar">
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    }
                >
                    {current?.message}
                </Alert>
            </Snackbar>
        </NotificationContext.Provider>
    )
}

export function useNotification() {
    const ctx = useContext(NotificationContext)
    if (!ctx) throw new Error('useNotification must be used within a NotificationProvider')
    return ctx
}
